import { faCloudUploadAlt, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../App.styles";
import ConfirmationModal from "../ConfirmationModal";
import Spinner from '../Spinner';
import useImageThumbnail from "../ImageThumbnail/useImageThumbnail";
import ImageThumbnailLoader from "../ImageThumbnail/ImageThumbnailLoader";
import { useUploadImageMutation } from "./NubImageUpload.state";
import {
    ActionWrapper,
    FileWrapper,
    ImageUploadWrapper,
    ImageContainer,
    ImageBody,
    UploadBody,
    UploadBtn,
    ImageLoader,
    ImagePreviewWrapper,
    ImagePlaceholder,
    ImageActionButton,
    ImageActionButtons,
    NubsItem,
} from "./NubImageUpload.style";
import { acceptedImages } from "../../consts/accepted-files";
import { toAssetUrl } from "../../lib/url";
import ModalGeneratePhoto from "../ModalGeneratePhoto";


// Component to render individual image using ImageThumbnailLoader
const NubImage = ({ url, isUploading, containerRef }) => {
    // Convert heic, dng, webp to jpg
    const imageUrl = (url || '').replace(/\.(heic|dng|webp)$/, '.jpg');
    const hookProps = useImageThumbnail({ thumb: imageUrl, isLoading: isUploading });
    const imageThumbnailLoaderProps = {
        isLoading: isUploading,
        className: 'nubs__image',
        width: 'full',
        height: 'full',
        src: imageUrl,
        alt: '',
        ...hookProps,
    };
    return (
        <NubsItem container={containerRef?.current} isImage={true}>
            <ImageThumbnailLoader {...imageThumbnailLoaderProps} />
        </NubsItem>
    );
};

const NubImageUpload = ({
    label,
    name,
    useQuery,
    maxImages = 9,
    withAi = false,
    stickerify = false,
    storageMode = "field",
    assetName,
    mediaPrefix,
}) => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [collection, setCollection] = useQuery();
    const { id } = collection || {};
    const inputFileRef = useRef(null);
    const containerRef = useRef(null);
    
    const handleFileLabel = (e) => {
        if (e.key !== "Enter" || inputFileRef.current === null) return;
        inputFileRef.current.click();
    };

    const {
        isLoading: isUploading,
        isGenerating,
        mutateAsync: uploadImage,
        createAiImage,
    } = useUploadImageMutation({
        useQuery,
        name,
        stickerify,
        storageMode,
        assetName,
        mediaPrefix,
    });

    // Parse comma-delimited image URLs
    const imageUrls = useMemo(() => {
        if (storageMode === "asset") {
            const assetUrl = (collection?.assets?.photos || [])
                .find(asset => asset.name === assetName)?.url;
            return assetUrl ? [assetUrl] : [];
        }

        const value = collection?.[name];
        if (!value) return [];
        return value.split(',')
            // filter out invalid URLs
            .filter(url => url.trim())
            // reverse the array so the most recent images are at the left
            .reverse();
    }, [assetName, collection, name, storageMode]);

    const upload = async (files) => {
        if (!files || files.length === 0) return;
        
        const filesToUpload = maxImages > 0
            ? Array.from(files).slice(0, maxImages - imageUrls.length)
            : Array.from(files);
        
        for (const file of filesToUpload) {
            // Validate file type
            const ext = `.${file.name.split('.').pop().toLowerCase()}`;
            if (!acceptedImages.includes(ext)) {
                continue;
            }
            await uploadImage(file);
        }
    };

    const onDelete = async (urlToDelete) => {
        try {
            if (storageMode === "asset") {
                await setCollection({
                    assets: {
                        ...collection.assets,
                        photos: (collection.assets?.photos || [])
                            .filter(asset => asset.name !== assetName),
                    },
                    [`${mediaPrefix}_media_uuid`]: null,
                    [`${mediaPrefix}_media_extension`]: null,
                });
                return;
            }

            const updatedUrls = imageUrls.filter(url => url !== urlToDelete);
            await setCollection({
                [name]: updatedUrls.join(','),
            });
        } catch { }
    };

    const placeholderLimit = maxImages || 5;
    const emptyPlaceholderCount = Math.max(placeholderLimit - imageUrls.length, 0);
    const fileId = name && id ? `${name}-${id}-upload` : `new-upload-${id}`;
    const isDisabled = maxImages > 0 && imageUrls.length >= maxImages;

    return (
        <ImageUploadWrapper className="form-outline">
            {label && <span className="form-label">{label}</span>}
            <ImageContainer ref={containerRef}>
                <ImageBody className="nubs__body" container={containerRef?.current}>
                    <UploadBody isDisabled={isDisabled} className="p-4" container={containerRef?.current} htmlFor={fileId} tabIndex={0} onKeyUp={handleFileLabel}>
                        <FontAwesomeIcon className="mb-4" icon={faCloudUploadAlt} />
                        <ActionWrapper>
                            <UploadBtn>
                                <FileWrapper>
                                    <input
                                        accept={acceptedImages.join(',')}
                                        className="form-control"
                                        id={fileId}
                                        type="file"
                                        multiple={maxImages > 1 || maxImages === 0}
                                        onChange={(evt) => {
                                            const { target } = evt;
                                            upload(maxImages === 1 ? [target.files[0]] : target.files);
                                            // Reset input so same file can be selected again
                                            target.value = '';
                                        }}
                                        ref={inputFileRef}
                                    />
                                </FileWrapper>
                                <Button disabled={isDisabled || isUploading || isGenerating} tag="span" color="primary" className="btn--flat">
                                    {t('Add Image')}
                                </Button>
                            </UploadBtn>
                            {
                                withAi
                                    ? (
                                        <Button
                                            disabled={isDisabled || isUploading || isGenerating}
                                            onClick={() => setShowModal(true)}
                                            className="btn--flat"
                                            color="primary"
                                        >
                                            {stickerify ? t('AI Generated Sticker') : t('AI Generated Image')}
                                        </Button>
                                    )
                                    : null
                            }
                        </ActionWrapper>
                        <ModalGeneratePhoto
                            show={showModal}
                            onDismiss={() => setShowModal(false)}
                            loading={isGenerating}
                            createAiPhoto={createAiImage}
                            stickerify={stickerify}
                        />
                        {imageUrls.length > 0 && (
                            <small className="text-muted">
                                {imageUrls.length} / {placeholderLimit}
                            </small>
                        )}
                        {isDisabled && (
                            <small className="text-muted">
                                {t('Delete some images if you want to add more.')}
                            </small>
                        )}
                    </UploadBody>
                    
                    {imageUrls.map((url, index) => (
                        <ImagePreviewWrapper key={`${url}-${index}`}>
                            <ImageActionButtons className="image-action__buttons">
                                <ConfirmationModal
                                    title={t("Confirm Deletion")}
                                    body={t("Are you sure you wish to delete?")}
                                    confirmLabel={t("Delete")}
                                    confirmAction={() => onDelete(url)}
                                >
                                    <ImageActionButton 
                                        title={t(`Delete Image`)} 
                                        color="link" 
                                        tabIndex={-1} 
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} />
                                    </ImageActionButton>
                                </ConfirmationModal>
                            </ImageActionButtons>
                            <NubImage url={toAssetUrl(url)} isUploading={false} containerRef={containerRef} />
                        </ImagePreviewWrapper>
                    ))}
                    {Array.from({ length: emptyPlaceholderCount }).map((_, index) => (
                        <ImagePlaceholder
                            key={`image-placeholder-${index}`}
                            container={containerRef?.current}
                        />
                    ))}
                </ImageBody>
            </ImageContainer>
            <ImageLoader isLoading={isUploading || isGenerating}>
                <Spinner />
            </ImageLoader>
        </ImageUploadWrapper>
    );
};

export default NubImageUpload;
